import { LitElement, html, css, styles, classMap } from '@hh/global.js'

class HHQuantitySelector extends LitElement {
  static properties = {
    key: {},
    label: {},
    min: { type: Number },
    max: { type: Number },
    initial: {
      type: Number,
    },
    _value: {
      state: true,
    },
    productId: {
      type: String,
      attribute: 'product-id',
    },
    isFull: {
      type: String,
      attribute: 'is-full',
    },
    isAdded: {
      type: String,
      attribute: 'is-added',
    },
  }

  constructor() {
    super()

    this.min = 1
    this.max = 99
    this.broadcast = this.broadcast.bind(this)
    this.productId = 0
    this.forceDisable = ''
    this.isAdded = false
    this.isTea = false
  }

  connectedCallback() {
    super.connectedCallback()
    this._value = this.initial
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('changeQuantity', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.productId,
          isTea: this.isTea,
          value: this._value,
          added: this.isAdded,
        },
      })
    )
  }

  increment() {
    if (this._value == this.max) {
      return
    }
    this._value += 1
  }

  decrement() {
    if (!this._value) {
      return
    }

    this._value -= 1
  }

  override(e) {
    if (e.target.value > this.max) {
      e.target.value = Math.max(0, this.max)
    }
    this._value = Math.max(0, e.target.value)
  }

  updateInputValue(e) {
    const $input = this.shadowRoot?.querySelector(`[name='input']`)
    const $formInput = document.querySelector(
      `#quantity-input--${this.productId}`
    )

    $input && ($input.value = this._value)
    $formInput && ($formInput.value = this._value)

    if (e.value > this.max) {
      e.value = Math.max(0, this.max)
    }
    this._value = Math.max(0, e.value)
  }

  addProduct(event) {
    const $input = this.shadowRoot?.querySelector(`[name='input']`)
    if ($input.value > this.max) $input.value = Math.max(0, this.max)
    this._value = $input.value
    const productCard = this.closest('.js-product-select-card')
    if (productCard.getAttribute('is-added') == 'true') {
      productCard.setAttribute('is-added', 'false')
      this.isAdded = 'false'
      this._value *= -1

      if (productCard.getAttribute('is-tea') == 'true') {
        this.isTea = true
      } else {
        this.isTea = false
      }
      this.broadcast()
      this._value = Math.abs(this._value)
    } else {
      productCard.setAttribute('is-added', 'true')
      this.isAdded = 'true'
      if (productCard.getAttribute('is-tea') == 'true') {
        this.isTea = true
      } else {
        this.isTea = false
      }
      this.broadcast()
    }
  }

  render() {
    return html`
      <span>
        <div class="wrapper">
          <button
            type="button"
            name="minus"
            @click="${this.decrement}"
            ?disabled="${this._value <= this.min ||
            this.isAdded == 'true' ||
            this.isFull == 'true'}"
          >
            <slot name="decrease"></slot>
          </button>
          <label
            class="visually-hidden"
            for="quantity-selector-${this.productId}"
          >
            ${this.label | 'Quantity'}
          </label>
          <input
            id="quantity-selector-${this.productId}"
            class="${classMap({
              'is-added': this.isAdded == 'true',
              input: true,
            })}"
            type="number"
            name="input"
            value="${this._value}"
            @change="${this.override}"
            ?disabled="${this.isAdded == 'true' || this.isFull == 'true'}"
          />
          <button
            type="button"
            name="plus"
            @click="${this.increment}"
            ?disabled="${this._value >= this.max ||
            this.isFull == 'true' ||
            this.isAdded == 'true'}"
          >
            <slot name="increase"></slot>
          </button>
          <button
            type="button"
            @click="${this.addProduct}"
            class="${classMap({
              btn: true,
              'is-added': this.isAdded == 'true',
            })}"
            ?disabled="${this.isFull == 'true'}"
          >
            <slot name="btn-add" class="btn-add"></slot>
            <slot name="btn-close" class="btn-close"></slot>
          </button>
        </div>
      </span>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      .wrapper {
        border: 1px solid var(--hh-border-color, #000);
        color: var(--hh-color, #000);
        background-color: var(--hh-background, transparent);
        max-width: var(--hh-max-width, 10rem);
        position: relative;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        padding: var(--hh-padding, 0 0.5rem);
      }

      .wrapper .input {
        color: currentColor;
        background-color: var(--hh-background, transparent);
        border-left: 1px solid var(--hh-border-color, #000) !important;
        border-right: 1px solid var(--hh-border-color, #000) !important;
        font: var(--hh-font);
        text-align: center;
        border: 0;
        padding: var(--hh-input-padding, 0 0.5rem);
        height: var(--hh-height, 2rem);
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
      }

      button {
        width: var(--hh-button-width, 2.5rem);
        flex-shrink: 0;
        border: 0;
        background-color: var(--hh-background-color, transparent);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--hh-color, #000);
      }

      .wrapper button[name='plus'] {
        border-left: 1px solid var(--hh-border-color, #000);
      }

      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--hh-color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--hh-color-background) inset !important;
      }

      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      .btn {
        background-color: var(--hh-btn-background, #000);
        border: 0;
        color: var(--hh-btn-color, #fff);
        cursor: pointer;
        display: block;
        font: var(--hh-font);
        max-width: var(--hh-btn-max-width, 180px);
        text-decoration: none;
        text-transform: uppercase;
        transition: all var(--hh-duration-default, 300ms);
        width: 100%;
        grid-column: 1 /4;
        padding: 0.7rem;
      }

      .btn:hover {
        background-color: var(--hh-btn-background-hover, #000);
      }

      .btn .btn-close {
        display: none;
      }

      .is-added .btn-add {
        display: none;
      }

      .is-added .btn-close {
        display: inline-block;
      }

      .btn.is-added {
        background-color: var(--hh-btn-background-active, #b09040);
      }

      .btn.is-added:hover {
        background-color: var(--hh-btn-background-active-hover, #000);
      }

      button[disabled],
      .input:disabled {
        cursor: default;
      }

      button[disabled] {
        opacity: 0.3;
      }

      .input.is-added {
        opacity: 1;
      }

      @media screen and (min-width: 750px) {
        .wrapper {
          grid-template-columns: 1fr 1fr 1fr var(--hh-cta-width, 6rem);
        }

        .btn {
          padding: 0;
          grid-column: unset;
        }
      }
    `,
  ]
}

customElements.define('hh-quantity-selector', HHQuantitySelector)
